import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { useSessionPresence } from "@/hooks/useSessionPresence";
import { Loader2, Menu } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { UserMenu } from "@/components/UserMenu";
import { ActiveUsers } from "@/components/session/ActiveUsers";
import { Phase3Intro } from "@/components/session/Phase3Intro";
import { DecisionMatrix } from "@/components/session/DecisionMatrix";
import { DominantStrategySummary } from "@/components/session/DominantStrategySummary";
import { Phase3Transition } from "@/components/session/Phase3Transition";
import { Phase1ContextPanel } from "@/components/session/Phase1ContextPanel";
import { PhaseNavigationDialog } from "@/components/session/PhaseNavigationDialog";
import { GlobalPhaseTimeline } from "@/components/session/GlobalPhaseTimeline";
import { DecisionCommitDialog } from "@/components/session/DecisionCommitDialog";
import { OptionsReferenceBar } from "@/components/session/OptionsReferenceBar";
import quentariLogo from "@/assets/quentari-logo.png";

interface DominantStrategy {
  type: "clear_winner" | "conditional" | "trade_off" | "hidden_winner";
  option_name?: string;
  option_id?: string;
  explanation: string;
  key_insights: string[];
}

const Phase3 = () => {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useAuth();
  const { activeUsers } = useSessionPresence(sessionId, user?.id || null);
  
  const [isLoading, setIsLoading] = useState(true);
  const [dominantStrategy, setDominantStrategy] = useState<DominantStrategy | null>(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isMatrixComplete, setIsMatrixComplete] = useState(false);
  const [showBackDialog, setShowBackDialog] = useState(false);
  const [showCommitDialog, setShowCommitDialog] = useState(false);
  const [tradeOffs, setTradeOffs] = useState<Array<{ optionName: string; criterion: string; impact: string }>>([]);
  const [excludedOptions, setExcludedOptions] = useState<Array<{ id: string; name: string; consequence: string; isDelayOption?: boolean }>>([]);
  
  useEffect(() => {
    loadSessionData();
  }, [sessionId]);

  const loadSessionData = async () => {
    if (!sessionId) {
      navigate("/onboarding");
      return;
    }

    try {
      const { data: session, error } = await supabase
        .from("decision_sessions")
        .select("phase")
        .eq("id", sessionId)
        .single();

      if (error) throw error;

      if (!session) {
        toast({
          title: "Session nicht gefunden",
          description: "Die Session konnte nicht geladen werden.",
          variant: "destructive",
        });
        navigate("/onboarding");
        return;
      }

      // Allow phase 2 or 3
      if (session.phase < 2) {
        toast({
          title: "Falsche Phase",
          description: "Diese Session ist noch nicht bereit für die Bewertung.",
          variant: "destructive",
        });
        navigate(`/app/session/${sessionId}`);
        return;
      }

      setIsLoading(false);
    } catch (error: any) {
      console.error("Error loading session:", error);
      toast({
        title: "Fehler",
        description: "Die Session-Daten konnten nicht geladen werden.",
        variant: "destructive",
      });
      navigate("/onboarding");
    }
  };

  const handleMatrixComplete = (strategy: DominantStrategy | null) => {
    setIsMatrixComplete(true);
    setIsAnalyzing(true);
    setTimeout(() => {
      setDominantStrategy(strategy);
      setIsAnalyzing(false);
    }, 500);
  };

  // Open commit dialog - load trade-offs and build excluded options list
  const handleRequestCommit = async () => {
    try {
      // Load trade-offs from session meta
      const { data: session } = await supabase
        .from("decision_sessions")
        .select("meta")
        .eq("id", sessionId)
        .single();
      
      const meta = (session?.meta || {}) as Record<string, unknown>;
      const tradeOffReasons = (meta.trade_off_reasons || {}) as Record<string, string>;
      
      // Get all scenarios for display
      const { data: scenarios } = await supabase
        .from("scenarios")
        .select("id, name, archetype, strategic_goal")
        .eq("session_id", sessionId!)
        .neq("scenario_type", "baseline");
      
      const scenarioNames: Record<string, string> = {};
      scenarios?.forEach(s => { scenarioNames[s.id] = s.name; });
      
      // Format trade-offs for display
      const formattedTradeOffs = Object.entries(tradeOffReasons).map(([key, reason]) => {
        const [optionId] = key.split('-');
        return {
          optionName: scenarioNames[optionId] || 'Option',
          criterion: key.split('-').slice(1).join('-'),
          impact: reason
        };
      });
      
      // Build excluded options (all non-dominant options) with sharp consequence language
      const formattedExcludedOptions = (scenarios || [])
        .filter(s => s.id !== dominantStrategy?.option_id)
        .map(s => {
          // Create sharp, cutting consequence based on archetype (no explanations - just cut)
          let consequence = "Keine Ressourcen für diese Richtung";
          let isDelayOption = false;
          
          if (s.archetype === "value_max") {
            consequence = "Keine Aktivitäten außerhalb des Kernsegments";
          } else if (s.archetype === "scaling") {
            consequence = "Kein Plattform- oder Abstraktionsprojekt";
            isDelayOption = true;
          } else if (s.archetype === "low_risk") {
            consequence = "Keine fokussierte Kernsegment-Validierung";
          }
          
          return {
            id: s.id,
            name: s.name,
            consequence,
            isDelayOption
          };
        });
      
      setTradeOffs(formattedTradeOffs);
      setExcludedOptions(formattedExcludedOptions);
      setShowCommitDialog(true);
    } catch (error) {
      console.error("Error loading trade-offs:", error);
      setShowCommitDialog(true);
    }
  };

  // Handle the actual commit
  const handleCommit = async (commitData: {
    committed_option_id: string;
    committed_option_name: string;
    validity_days: 30 | 60 | 90;
    owner_name: string;
    review_date: string;
  }) => {
    try {
      // Get current session meta
      const { data: session } = await supabase
        .from("decision_sessions")
        .select("meta")
        .eq("id", sessionId)
        .single();
      
      const currentMeta = (session?.meta || {}) as Record<string, unknown>;
      
      // Save commit data to session meta
      await supabase
        .from("decision_sessions")
        .update({ 
          phase: 3, 
          subphase: "3.4",
          meta: {
            ...currentMeta,
            decision_commit: {
              ...commitData,
              committed_at: new Date().toISOString(),
            }
          }
        })
        .eq("id", sessionId);

      setShowCommitDialog(false);
      
      toast({
        title: "Entscheidung committed",
        description: `${commitData.committed_option_name} wurde verbindlich festgelegt.`,
      });
      
      navigate(`/app/session/${sessionId}/report`);
    } catch (error: any) {
      console.error("Error committing decision:", error);
      toast({
        title: "Fehler",
        description: "Die Entscheidung konnte nicht gespeichert werden.",
        variant: "destructive",
      });
    }
  };

  const [targetPhase, setTargetPhase] = useState<number | null>(null);

  const handlePhaseNavigation = (phase: number) => {
    setTargetPhase(phase);
    setShowBackDialog(true);
  };

  const handleConfirmNavigation = async () => {
    if (!sessionId || targetPhase === null) return;
    
    try {
      if (targetPhase === 1) {
        await supabase
          .from("decision_sessions")
          .update({ phase: 1, subphase: "1.4" })
          .eq("id", sessionId);
        navigate(`/app/session/${sessionId}`);
      } else if (targetPhase === 2) {
        await supabase
          .from("decision_sessions")
          .update({ phase: 2, subphase: "2.0" })
          .eq("id", sessionId);
        navigate(`/app/session/${sessionId}/phase2`);
      } else if (targetPhase === 4) {
        await supabase
          .from("decision_sessions")
          .update({ phase: 3, subphase: "3.4" })
          .eq("id", sessionId);
        navigate(`/app/session/${sessionId}/report`);
      }
    } catch (error: any) {
      console.error("Error navigating to phase:", error);
      toast({
        title: "Fehler",
        description: "Navigation fehlgeschlagen.",
        variant: "destructive",
      });
    }
  };

  const getPhaseLabel = (phase: number) => {
    const labels: Record<number, string> = {
      1: "Phase 1 – Analyse",
      2: "Phase 2 – Optionen",
      3: "Phase 3 – Bewertung",
      4: "Report"
    };
    return labels[phase] || `Phase ${phase}`;
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center relative overflow-hidden">
        {/* Background decorative elements */}
        <div className="absolute inset-0 pointer-events-none">
          <div 
            className="absolute top-0 left-0 w-full h-full opacity-[0.02]"
            style={{
              backgroundImage: `
                radial-gradient(ellipse 60% 40% at 10% 20%, hsl(var(--primary)) 0%, transparent 50%),
                radial-gradient(ellipse 50% 50% at 90% 80%, hsl(var(--primary)) 0%, transparent 50%)
              `,
            }}
          />
        </div>
        <div className="text-center relative z-10">
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center mx-auto mb-4">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
          <p className="text-muted-foreground">Phase 3 wird geladen...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background relative overflow-hidden">
      {/* Background decorative elements */}
      <div className="fixed inset-0 pointer-events-none">
        <div 
          className="absolute top-0 left-0 w-full h-full opacity-[0.02]"
          style={{
            backgroundImage: `
              radial-gradient(ellipse 60% 40% at 10% 20%, hsl(var(--primary)) 0%, transparent 50%),
              radial-gradient(ellipse 50% 50% at 90% 80%, hsl(var(--primary)) 0%, transparent 50%)
            `,
          }}
        />
        <div 
          className="absolute inset-0 opacity-[0.015]"
          style={{
            backgroundImage: `linear-gradient(hsl(var(--primary)) 1px, transparent 1px), linear-gradient(90deg, hsl(var(--primary)) 1px, transparent 1px)`,
            backgroundSize: '60px 60px',
          }}
        />
      </div>

      <header className="sticky top-0 z-50 w-full bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 border-b border-border">
        <div className="container flex h-16 items-center justify-between max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex items-center gap-2 md:gap-4">
            <img 
              src={quentariLogo} 
              alt="Quentari" 
              className="h-8 w-auto cursor-pointer transition-transform duration-300 hover:scale-105" 
              onClick={() => navigate('/dashboard')}
            />
            <div className="h-6 w-px bg-border hidden sm:block" />
            <GlobalPhaseTimeline
              currentPhase={3}
              sessionId={sessionId!}
              onPhaseClick={handlePhaseNavigation}
            />
          </div>
          
          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-4">
            <ActiveUsers users={activeUsers} />
            <UserMenu />
          </div>

          {/* Mobile Navigation */}
          <div className="flex md:hidden items-center gap-2">
            <Sheet>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="hover:bg-primary/10 hover:text-primary">
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[300px] border-border/50">
                <div className="flex flex-col gap-6 mt-6">
                  <div className="p-4 rounded-xl bg-gradient-to-br from-primary/10 to-primary/5 border border-primary/20">
                    <span className="text-sm font-medium">Phasen-Navigation</span>
                    <div className="mt-3">
                      <GlobalPhaseTimeline
                        currentPhase={3}
                        sessionId={sessionId!}
                        onPhaseClick={handlePhaseNavigation}
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <span className="text-sm font-medium">Aktive Nutzer</span>
                    <ActiveUsers users={activeUsers} />
                  </div>
                  <div className="border-t border-border/50 pt-4">
                    <UserMenu />
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </header>

      <main className="container max-w-5xl mx-auto px-4 md:px-6 py-8 md:py-12 relative z-10">

        <div className="space-y-6">
          {/* Phase 1 Context Panel */}
          <Phase1ContextPanel sessionId={sessionId!} />

          {/* Options Reference Bar - sticky above matrix */}
          <OptionsReferenceBar sessionId={sessionId!} />

          {/* 3.1 - Intro */}
          <Phase3Intro />

          {/* 3.2 - Decision Matrix */}
          <div className="space-y-4">
            <DecisionMatrix 
              sessionId={sessionId!} 
              onMatrixComplete={handleMatrixComplete}
            />
          </div>

          {/* 3.3 - Dominant Strategy (shows after matrix is complete) */}
          {(dominantStrategy || isAnalyzing) && (
            <div className="animate-fade-in">
              <DominantStrategySummary 
                dominantStrategy={dominantStrategy}
                isLoading={isAnalyzing}
              />
            </div>
          )}

          {/* 3.4 - Transition */}
          <Phase3Transition 
            onContinue={handleRequestCommit} 
            isDisabled={!isMatrixComplete}
            dominantOptionName={dominantStrategy?.option_name}
          />
        </div>
      </main>

      {/* Decision Commit Dialog */}
      <DecisionCommitDialog
        open={showCommitDialog}
        onOpenChange={setShowCommitDialog}
        dominantOptionId={dominantStrategy?.option_id || ""}
        dominantOptionName={dominantStrategy?.option_name || "Dominante Option"}
        tradeOffs={tradeOffs}
        excludedOptions={excludedOptions}
        onCommit={handleCommit}
      />

      {/* Phase Navigation Dialog */}
      <PhaseNavigationDialog
        open={showBackDialog}
        onOpenChange={setShowBackDialog}
        onConfirm={handleConfirmNavigation}
        fromPhase="Phase 3 – Bewertung"
        toPhase={getPhaseLabel(targetPhase || 2)}
      />
    </div>
  );
};

export default Phase3;
